<template>
  <div v-if="isDesktop" class="table-responsive">
    <table class="table is-fullwidth is-hoverable">
      <thead>
        <tr>
          <th scope="col">{{ labels.type }}</th>
          <th scope="col">{{ labels.stockName }}</th>
          <th scope="col" class="positions-center-column">{{ labels.stockCode }}</th>
          <th scope="col" class="positions-number-column">{{ labels.myPositions }}</th>
          <th scope="col" class="positions-number-column">{{ labels.purchasePrice }}</th>
          <th scope="col" class="positions-number-column">{{ labels.totalAmount }}</th>
          <th scope="col" class="positions-number-column">{{ labels.marketPrice }}</th>
          <th scope="col" class="positions-number-column">{{ labels.value }}</th>
          <th scope="col" class="positions-number-column">{{ labels.return }}</th>
          <th v-if="isBulgarian" scope="col" class="positions-number-column">{{ labels.return }}&nbsp;%</th>
          <th v-else scope="col" class="positions-number-column">%{{ labels.return }}</th>
          <th scope="col" class="positions-action-column"></th>
        </tr>
      </thead>
      <tbody>
        <tr class="center_verticaly" v-for="stock in sortedStocks" :key="stock.sc">
          <td>{{ stockTypeResolver(stock.stid) }}</td>
          <td v-if="isBulgarian">{{ stockName(stock) }}</td>
          <td v-if="isEnglish">{{ stock.nEN }}</td>
          <td class="positions-center-column">{{ stock.sc }}</td>
          <td class="equity_align_right">{{ parseNumber(positionsAmountResolver(stock), 0) }}</td>
          <td v-if="isTransferPosition(stock)" class="equity_align_right">n/a</td>
          <td v-else-if="2 !== stock.stid" class="equity_align_right">{{ formatPrice(stock.ap, stock.cc).replace(' ', '&nbsp;') }}</td>
          <td v-else class="equity_align_right">{{ formatPrice(stock.ap, '%').replace(' ', '&nbsp;') }}</td>
          <td class="equity_align_right">{{ formatPrice(tradePurchasePriceResolver(stock), stock.cc).replace(' ', '&nbsp;') }}</td>
          <td v-if="2 !== stock.stid" class="equity_align_right">{{ formatPrice(marketUnitPrice(stock), stock.cc).replace(' ', '&nbsp;') }}</td>
          <td v-else class="equity_align_right">{{ formatPrice(marketUnitPrice(stock), '%').replace(' ', '&nbsp;') }}</td>
          <td class="equity_align_right">{{ formatPrice(tradeMarketPriceResolver(stock), stock.cc).replace(' ', '&nbsp;') }}</td>
          <td v-if="isTransferPosition(stock)" class="equity_align_right">n/a</td>
          <td v-else class="equity_align_right" :class="returnClass(stock)">{{ parseNumber(returnValue(stock)) }}</td>
          <td v-if="isTransferPosition(stock)" class="equity_align_right">n/a</td>
          <td v-else class="equity_align_right" :class="returnClass(stock)">
            {{ parseNumber(returnPercent(stock)) }}%
          </td>
          <td class="positions-action-column">
            <div class="btn-group">
              <button type="button" class="btn btn-primary" :title="labels.information" :aria-label="labels.information" @click.prevent="$emit('open-trades', stock)">
                <svg class="positions-action-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="10" x2="12" y2="16"></line>
                  <circle cx="12" cy="7" r="0.75"></circle>
                </svg>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div v-else>
    <div class="card" v-for="stock in sortedStocks" :key="stock.sc">
      <div class="card-body xsmall-font-size">
        <div class="container p-0">
          <div class="row justify-content-between">
            <div class="col-6">
              <span v-if="isBulgarian"><strong>{{ stockName(stock) }}</strong></span>
              <span v-if="isEnglish"><strong>{{ stock.nEN }}</strong></span>
              <br />
              {{ stockTypeResolver(stock.stid) }}<br />
              {{ stock.sc.toUpperCase() }}<br />
              {{ labels.amount }}: {{ parseNumber(stock.am, 0) }}
            </div>
            <div class="col-6 text-end my-auto">
              <div class="btn-group">
                <button type="button" class="btn btn-sm btn-primary" :title="labels.information" :aria-label="labels.information" @click.prevent="$emit('open-trades', stock)">
                  <svg class="positions-action-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="10" x2="12" y2="16"></line>
                    <circle cx="12" cy="7" r="0.75"></circle>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  emits: ['open-trades', 'open-details'],
  props: {
    stocks: { type: Array, required: true },
    windowWidth: { type: Number, required: true },
    userLanguage: { type: String, required: true },
    labels: { type: Object, required: true },
    stockTypeResolver: { type: Function, required: true },
    positionsAmountResolver: { type: Function, required: true },
    tradePurchasePriceResolver: { type: Function, required: true },
    tradeMarketPriceResolver: { type: Function, required: true },
    percentDiffResolver: { type: Function, required: true },
    formatPrice: { type: Function, required: true },
    parseNumber: { type: Function, required: true }
  },
  computed: {
    sortedStocks() {
      return [...this.stocks].sort((left, right) => String(left?.sc ?? "").localeCompare(
        String(right?.sc ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      ));
    },
    quoteUpIcon() {
      return quoteUpIcon;
    },
    isDesktop() {
      return 1200 < this.windowWidth;
    },
    isBulgarian() {
      return 0 === 'BG'.localeCompare(this.userLanguage);
    },
    isEnglish() {
      return 0 === 'EN'.localeCompare(this.userLanguage);
    }
  },
  methods: {
    stockName(stock) {
      return stock.nBG ? stock.nBG : stock.nEN;
    },
    marketUnitPrice(stock) {
      return stock.ldp
        ? stock.ldp
        : stock.clp
          ? stock.clp
          : stock.mp;
    },
    isTransferPosition(stock) {
      const averagePrice = Number(stock?.ap);
      const hasTrades = Array.isArray(stock?.t)
        && stock.t.some(trade => "" !== trade?.oid?.toString());
      return !hasTrades && (!Number.isFinite(averagePrice) || 0 === averagePrice);
    },
    returnValue(stock) {
      return this.tradeMarketPriceResolver(stock) - this.tradePurchasePriceResolver(stock);
    },
    returnPercent(stock) {
      return this.percentDiffResolver(this.tradeMarketPriceResolver(stock), this.tradePurchasePriceResolver(stock));
    },
    returnClass(stock) {
      const value = this.returnValue(stock);

      return {
        greenColor: value > 0,
        redColor: value < 0
      };
    }
  }
};
</script>

<style scoped>
.table td,
.table th {
  font-size: 1rem;
}

.table th:first-child,
.table td:first-child {
  white-space: nowrap;
}

.equity_align_right {
  vertical-align: middle;
  text-align: right;
}

.positions-number-column {
  text-align: right;
  vertical-align: middle;
  white-space: nowrap;
}

.positions-center-column {
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

.positions-action-column {
  text-align: center;
  vertical-align: middle;
  width: 4rem;
}

.center_verticaly {
  vertical-align: middle;
}

.greenColor {
  color: #189e18;
}

.redColor {
  color: #ff0000;
}

.xsmall-font-size {
  font-size: x-small;
}

.positions-action-icon {
  display: block;
  fill: none;
  height: 1rem;
  margin: 0 auto;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
  width: 1rem;
}

.positions-action-icon circle:last-child {
  fill: currentColor;
  stroke: none;
}

</style>
